import { useCallback } from "react"
import {
	NodeViewContent,
	NodeViewWrapper,
	ReactNodeViewRenderer,
} from "@tiptap/react"

import { TabsExtension } from "@productlane/lib/src/tiptap/extensions/tabs-extension"

import type { Node as ProseMirrorNode } from "@tiptap/pm/model"
import type { NodeViewProps } from "@tiptap/react"
import { PanelBar } from "./panel-bar"
import { usePanelGroup } from "./use-panel-group"

const tabLabel = (child: ProseMirrorNode, index: number): string =>
	typeof child.attrs.title === "string" && child.attrs.title.trim()
		? child.attrs.title
		: `Tab ${index + 1}`

function TabsNodeView({ node, editor, getPos }: NodeViewProps) {
	const group = usePanelGroup({ node, editor, getPos, label: tabLabel })

	const onAdd = useCallback(() => {
		const pos = getPos()
		if (pos === undefined) return
		editor.commands.addTab(pos, `Tab ${node.childCount + 1}`)
	}, [editor, getPos, node.childCount])

	const onRemove = useCallback(
		(index: number) => {
			const pos = group.positionOf(index)
			if (pos === undefined) return
			editor.commands.removeTab(pos)
		},
		[editor, group],
	)

	return (
		<NodeViewWrapper className="docs-panels" data-panels="tabs">
			<PanelBar
				{...group}
				addLabel="Add tab"
				removeLabel="Remove tab"
				onAdd={onAdd}
				onRemove={onRemove}
			/>
			<NodeViewContent data-panel-group="tabs" data-active={group.active} />
		</NodeViewWrapper>
	)
}

export const TabsWithNodeView = TabsExtension.extend({
	addNodeView() {
		return ReactNodeViewRenderer(TabsNodeView)
	},
})
